import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseOutlined, SendOutlined, RobotOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { planApi } from '../../api/plan.api.js';
import { supportChatApi } from '../../api/support-chat.api.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { PAYMENT_ENABLED } from '../../constants/payment.js';
import {
  BOT_GREETING,
  BOT_FALLBACK,
  BOT_TOPICS,
  SUPPORT_CHAT_ACTIONS,
  SUPPORT_ZALO_URL,
  findBotTopic,
} from '../../constants/supportBotData.js';
import './support-chat.css';

// Không hiện nút ở các trang quản trị
const HIDDEN_PATH_PREFIXES = ['/admin'];

const formatVnd = (price) =>
  price === 0 ? 'Miễn phí' : new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);

// Lịch sử chat lưu ở backend (15 ngày kể từ tin cuối, SUPPORT_CHAT_RETENTION_DAYS). Trình duyệt chỉ giữ visitorId do backend cấp.
const VISITOR_KEY = 'support_chat_visitor';
const readVisitorId = () => {
  try {
    return localStorage.getItem(VISITOR_KEY) || '';
  } catch {
    return '';
  }
};
const saveVisitorId = (id) => {
  try {
    localStorage.setItem(VISITOR_KEY, id);
  } catch {
    // Trình duyệt chặn lưu trữ: vẫn chat được, chỉ không nhớ lịch sử sau khi tải lại trang
  }
};

// Đang chat với tư vấn viên: hỏi tin mới 4 giây/lần (cả khi thu nhỏ khung chat, để báo có tin mới).
// Đang là BOT và khung chat mở: 12 giây/lần, để thấy khi tư vấn viên chủ động vào chat.
const POLL_HUMAN_MS = 4000;
const POLL_BOT_MS = 12000;

const SENDER_TO_FROM = { USER: 'user', BOT: 'bot', AGENT: 'agent', SYSTEM: 'system' };

// Nút gợi ý lưu ở backend: id (AI) hoặc {label, to|href} (BOT từ khóa)
const toActionObjects = (actions = []) =>
  actions.map((a) => (typeof a === 'string' ? SUPPORT_CHAT_ACTIONS[a] : a)).filter(Boolean);
const toActionLog = (actions = []) =>
  actions.map(({ label, to, href }) => (to ? { label, to } : { label, href })).filter((a) => a.to || a.href);

let tempSeq = 0;
const GREETING = { id: 'greeting', from: 'bot', text: BOT_GREETING, actions: [] };
// Tin chưa lưu backend có id dạng chuỗi 'tmp-n'; tin đã lưu dùng id số của backend
const localMessage = (from, text, actions = []) => ({ id: `tmp-${++tempSeq}`, from, text, actions });
const fromServer = (m) => ({
  id: m.id,
  from: SENDER_TO_FROM[m.sender] || 'bot',
  text: m.text,
  actions: toActionObjects(m.actions),
  agentName: m.agentName,
});

/** Gộp tin từ backend vào danh sách: bỏ tin tạm đã được lưu, không trùng id, sắp theo id */
function mergeMessages(prev, serverMessages, removeIds = []) {
  const kept = prev.filter((m) => !removeIds.includes(m.id));
  const known = new Set(kept.filter((m) => typeof m.id === 'number').map((m) => m.id));
  const saved = [
    ...kept.filter((m) => typeof m.id === 'number'),
    ...serverMessages.filter((m) => !known.has(m.id)).map(fromServer),
  ].sort((a, b) => a.id - b.id);
  const temps = kept.filter((m) => typeof m.id === 'string' && m.id !== 'greeting');
  return [GREETING, ...saved, ...temps];
}

const lastSavedId = (messages) => messages.reduce((max, m) => (typeof m.id === 'number' && m.id > max ? m.id : max), 0);

const BOT_CONVERSATION = { mode: 'BOT', agentName: null, humanRequested: false };

// Trạng thái ở cấp module (ngoài component): chuyển trang, kể cả sang trang học bài không có khung chat, vẫn giữ
const chatStore = {
  messages: null,
  visitorId: readVisitorId(),
  conversation: BOT_CONVERSATION,
  aiEnabled: false,
  loadedFor: null, // tài khoản (hoặc 'guest') đã tải lịch sử; đổi tài khoản thì tải lại
};

const setVisitorId = (id) => {
  if (!id || id === chatStore.visitorId) return;
  chatStore.visitorId = id;
  saveVisitorId(id);
};

export function SupportChatWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userKey = user?.id || 'guest';
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState(() => chatStore.messages || [GREETING]);
  const [conversation, setConversation] = useState(chatStore.conversation);
  const [aiEnabled, setAiEnabled] = useState(chatStore.aiEnabled);
  const busyRef = useRef(false); // đang gửi tin: tạm ngừng hỏi tin mới để không hiện trùng
  const openRef = useRef(open);
  const plansRef = useRef(null); // cache bảng giá, chỉ gọi API 1 lần
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  const isHuman = conversation.mode === 'HUMAN';
  const hasSaved = messages.some((m) => typeof m.id === 'number');

  useEffect(() => {
    chatStore.messages = messages;
  }, [messages]);

  useEffect(() => {
    chatStore.conversation = conversation;
  }, [conversation]);

  useEffect(() => {
    chatStore.aiEnabled = aiEnabled;
  }, [aiEnabled]);

  useEffect(() => {
    openRef.current = open;
    if (open) setUnread(0);
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  /** Áp kết quả backend: visitorId, trạng thái tư vấn viên, tin mới (thay tin tạm removeIds) */
  const applyServer = useCallback((data, removeIds = []) => {
    if (!data) return;
    setVisitorId(data.visitorId);
    if (data.conversation) setConversation(data.conversation);
    if (data.messages?.length || removeIds.length) {
      setMessages((prev) => mergeMessages(prev, data.messages || [], removeIds));
    }
  }, []);

  // Tải lịch sử: khi đã từng chat trên trình duyệt này / đã đăng nhập (có thể chat ở thiết bị khác), hoặc lần đầu mở khung chat.
  // Đổi tài khoản (đăng nhập/đăng xuất) thì tải lại: hội thoại của tài khoản khác không hiện cho người dùng sau.
  const loadConversation = useCallback(async () => {
    chatStore.loadedFor = userKey;
    try {
      const res = await supportChatApi.openConversation(chatStore.visitorId);
      const data = res.data;
      setVisitorId(data.visitorId);
      setAiEnabled(!!data.aiEnabled);
      setConversation(data.conversation || BOT_CONVERSATION);
      setMessages(mergeMessages([], data.messages || []));
    } catch {
      // Backend lỗi: vẫn chat được bằng BOT từ khóa, không lưu lịch sử
      supportChatApi
        .getStatus()
        .then((r) => setAiEnabled(!!r.data?.aiEnabled))
        .catch(() => setAiEnabled(false));
    }
  }, [userKey]);

  useEffect(() => {
    if (chatStore.loadedFor === userKey) return;
    if (chatStore.loadedFor !== null || open || chatStore.visitorId || user) {
      loadConversation();
    }
  }, [userKey, open, user, loadConversation]);

  // Hỏi tin mới định kỳ: tư vấn viên trả lời, vào hoặc rời cuộc trò chuyện
  const isActive = isHuman || conversation.humanRequested;
  useEffect(() => {
    if (!hasSaved || (!open && !isActive)) return undefined;
    const timer = setInterval(async () => {
      if (document.hidden || busyRef.current || !chatStore.visitorId) return;
      try {
        const res = await supportChatApi.getUpdates(chatStore.visitorId, lastSavedId(chatStore.messages || []));
        const data = res.data;
        if (data.visitorId !== chatStore.visitorId) {
          loadConversation();
          return;
        }
        if (busyRef.current) return;
        applyServer(data);
        const incoming = (data.messages || []).filter((m) => m.sender === 'AGENT').length;
        if (incoming && !openRef.current) setUnread((n) => n + incoming);
      } catch {
        // Mất mạng tạm thời: lượt sau hỏi lại
      }
    }, isActive ? POLL_HUMAN_MS : POLL_BOT_MS);
    return () => clearInterval(timer);
  }, [hasSaved, open, isActive, applyServer, loadConversation]);

  if (HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  // Câu trả lời về giá lấy từ API /plans để luôn khớp giá admin đang cấu hình
  const buildPricingAnswer = async (topic) => {
    try {
      if (!plansRef.current) {
        const res = await planApi.getAllPlans();
        plansRef.current = (res.data || []).filter((p) => p.code !== 'DOI_TAC');
      }
      if (!plansRef.current.length) return { text: topic.answer, actions: topic.actions };
      const lines = plansRef.current.map(
        (p) => `• ${p.name}: ${formatVnd(p.price)}${p.price > 0 ? ` / ${p.durationMonths} tháng` : ''}`
      );
      return { text: `Bảng học phí hiện tại:\n${lines.join('\n')}\n\nBấm nút dưới để xem quyền lợi từng gói.`, actions: topic.actions };
    } catch {
      return { text: topic.answer, actions: topic.actions };
    }
  };

  /** Câu trả lời của BOT từ khóa (chạy trên trình duyệt, không tốn phí AI) */
  const buildTopicAnswer = async (topic) => {
    if (!topic) return { text: BOT_FALLBACK, actions: [] };
    if (topic.id === 'pricing' && PAYMENT_ENABLED) return buildPricingAnswer(topic);
    return { text: topic.answer, actions: topic.actions || [] };
  };

  const pagePath = location.pathname.slice(0, 300);

  /** Hiện câu trả lời BOT từ khóa (trễ ngắn cho cảm giác đang trả lời) rồi gửi về backend lưu lịch sử */
  const answerWithKeywordBot = async (topic, userEntry = null) => {
    const answer = await buildTopicAnswer(topic);
    await new Promise((r) => setTimeout(r, 450));
    const temp = localMessage('bot', answer.text, answer.actions || []);
    setMessages((prev) => [...prev, temp]);
    const entries = [
      ...(userEntry ? [{ sender: 'USER', text: userEntry.text, actions: [] }] : []),
      { sender: 'BOT', text: answer.text, actions: toActionLog(answer.actions) },
    ];
    try {
      const res = await supportChatApi.logExchange({ visitorId: chatStore.visitorId || undefined, entries, pagePath });
      applyServer(res.data, [temp.id, ...(userEntry ? [userEntry.id] : [])]);
    } catch {
      // Không lưu được: tin vẫn hiện trên màn hình
    }
  };

  // Nút chủ đề: luôn trả lời bằng BOT từ khóa (tức thì, không tốn phí AI), lưu cả câu hỏi và câu trả lời
  const askTopic = async (topic) => {
    if (typing) return;
    const temp = localMessage('user', topic.label);
    setMessages((prev) => [...prev, temp]);
    setTyping(true);
    busyRef.current = true;
    try {
      await answerWithKeywordBot(topic, temp);
    } finally {
      busyRef.current = false;
      setTyping(false);
    }
  };

  // Câu khách tự gõ: lưu backend. BOT: backend hỏi AI kèm lịch sử; AI tắt/lỗi thì trả lời bằng BOT từ khóa.
  // Đang có tư vấn viên: chỉ lưu, tư vấn viên trả lời (khung chat hỏi tin mới định kỳ).
  const sendText = async (text) => {
    const temp = localMessage('user', text);
    setMessages((prev) => [...prev, temp]);
    busyRef.current = true;
    if (!isHuman) setTyping(true);
    const send = (useAi) =>
      supportChatApi.sendMessage({ visitorId: chatStore.visitorId || undefined, text, useAi, pagePath });
    try {
      const useAi = aiEnabled && !isHuman;
      let res;
      try {
        res = await send(useAi);
      } catch (err) {
        // Hết lượt hỏi AI theo IP: gửi lại không dùng AI để vẫn lưu tin, BOT từ khóa trả lời
        if (!(useAi && err.statusCode === 429)) throw err;
        res = await send(false);
      }
      applyServer(res.data, [temp.id]);
      if (res.data.fallback && res.data.conversation?.mode !== 'HUMAN') await answerWithKeywordBot(findBotTopic(text));
    } catch {
      if (isHuman) {
        setMessages((prev) => [
          ...prev,
          localMessage('system', 'Chưa gửi được tin nhắn. Anh/chị thử lại hoặc chat Zalo với tư vấn viên.'),
        ]);
      } else {
        const answer = await buildTopicAnswer(findBotTopic(text));
        setMessages((prev) => [...prev, localMessage('bot', answer.text, answer.actions || [])]);
      }
    } finally {
      busyRef.current = false;
      setTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || typing) return;
    setInput('');
    sendText(text);
  };

  const handleRequestHuman = async () => {
    if (typing || isActive) return;
    busyRef.current = true;
    setTyping(true);
    try {
      const res = await supportChatApi.requestHuman({ visitorId: chatStore.visitorId || undefined, pagePath });
      applyServer(res.data);
    } catch {
      setMessages((prev) => [...prev, localMessage('bot', BOT_FALLBACK, [SUPPORT_CHAT_ACTIONS.zalo])]);
    } finally {
      busyRef.current = false;
      setTyping(false);
    }
  };

  const handleAction = (action) => {
    if (action.to) {
      navigate(action.to);
      // Màn hình nhỏ: khung chat che trang, đóng lại để khách thấy trang vừa mở
      if (window.innerWidth < 576) setOpen(false);
    }
  };

  const subtitle = isHuman
    ? `${conversation.agentName || 'Tư vấn viên'} đang hỗ trợ`
    : conversation.humanRequested
      ? 'Đang chờ tư vấn viên vào chat'
      : `${aiEnabled ? 'Trợ lý AI' : 'Trả lời tự động'} · Tư vấn viên qua Zalo`;

  return (
    <div className="support-chat">
      {open && (
        <div className="support-chat-panel" role="dialog" aria-label="Trợ lý hỗ trợ khách hàng">
          <div className={`support-chat-header${isHuman ? ' support-chat-header--human' : ''}`}>
            <div className="support-chat-avatar">{isHuman ? <CustomerServiceOutlined /> : <RobotOutlined />}</div>
            <div className="support-chat-heading">
              <div className="support-chat-title">Trợ lý Thế Thảo Pharma</div>
              <div className="support-chat-subtitle">{subtitle}</div>
            </div>
            <button type="button" className="support-chat-close" onClick={() => setOpen(false)} aria-label="Đóng">
              <CloseOutlined />
            </button>
          </div>

          <div className="support-chat-body" ref={bodyRef}>
            {messages.map((m) =>
              m.from === 'system' ? (
                <div key={m.id} className="support-chat-system">
                  {m.text}
                </div>
              ) : (
                <div key={m.id} className={`support-chat-msg support-chat-msg--${m.from === 'user' ? 'user' : 'bot'}`}>
                  {m.from === 'agent' && <div className="support-chat-sender">{m.agentName || 'Tư vấn viên'}</div>}
                  <div className={`support-chat-bubble${m.from === 'agent' ? ' support-chat-bubble--agent' : ''}`}>{m.text}</div>
                  {m.actions.length > 0 && (
                    <div className="support-chat-actions">
                      {m.actions.map((a) =>
                        a.href ? (
                          <a
                            key={a.label}
                            className="support-chat-action"
                            href={a.href}
                            target={a.href.startsWith('http') ? '_blank' : undefined}
                            rel="noopener noreferrer"
                          >
                            {a.label}
                          </a>
                        ) : (
                          <button key={a.label} type="button" className="support-chat-action" onClick={() => handleAction(a)}>
                            {a.label}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              )
            )}
            {typing && (
              <div className="support-chat-msg support-chat-msg--bot">
                <div className="support-chat-bubble support-chat-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          <div className="support-chat-chips">
            {!isActive && (
              <button
                type="button"
                className="support-chat-chip support-chat-chip--human"
                onClick={handleRequestHuman}
                disabled={typing}
              >
                Gặp tư vấn viên
              </button>
            )}
            {BOT_TOPICS.map((t) => (
              <button key={t.id} type="button" className="support-chat-chip" onClick={() => askTopic(t)} disabled={typing}>
                {t.label}
              </button>
            ))}
          </div>

          <form className="support-chat-input" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isHuman ? 'Nhắn cho tư vấn viên…' : 'Nhập câu hỏi…'}
              maxLength={300}
            />
            <button type="submit" disabled={!input.trim() || typing} aria-label="Gửi">
              <SendOutlined />
            </button>
          </form>

          <a className="support-chat-zalo" href={SUPPORT_ZALO_URL} target="_blank" rel="noopener noreferrer">
            <span className="support-chat-zalo-badge">Zalo</span> Chat Zalo với tư vấn viên
          </a>
        </div>
      )}

      <button
        type="button"
        className={`support-chat-fab${open ? ' support-chat-fab--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Đóng khung hỗ trợ' : unread ? `Mở khung hỗ trợ, ${unread} tin mới` : 'Mở khung hỗ trợ'}
      >
        {open ? <CloseOutlined /> : <span className="support-chat-fab-text">Zalo</span>}
        {!open && unread > 0 && <span className="support-chat-fab-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
    </div>
  );
}
